import { useCallback, useEffect, useRef, useState } from 'react';
import { isEqual, isFunction } from 'lodash';
import { preferenceKeyForUser } from 'common/constants/userPreferences';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import {
  getPreference,
  migrateLegacyMultiUserPreference,
  setPreference,
  subscribePreferenceChanges,
} from 'services/storage/preferencesIndexedDB';

interface PreferenceState<T> {
  isReady: boolean;
  storageKey?: string;
  value: T;
}

const useLocalStorageByUser = <T>(
  key: string,
  initialValue: T
): readonly [T, (value: T | ((previousValue: T) => T)) => void, boolean] => {
  const [{ user }] = useStore() as unknown as UseStoreValues;
  const userId = user?.id as number | undefined;
  const storageKey = userId !== undefined ? preferenceKeyForUser(key, userId) : undefined;

  // Capture the initial value once so inline [] / {} literals don't invalidate the effect.
  const initialValueRef = useRef(initialValue);

  const [preferenceState, setPreferenceState] = useState<PreferenceState<T>>({
    isReady: false,
    value: initialValue,
  });
  const isCurrentUserPreference = preferenceState.storageKey === storageKey;
  const valueByUser = isCurrentUserPreference ? preferenceState.value : initialValueRef.current;
  const isReady = isCurrentUserPreference && preferenceState.isReady;
  const valueByUserRef = useRef(valueByUser);
  valueByUserRef.current = valueByUser;

  useEffect(() => {
    const iv = initialValueRef.current;

    if (userId === undefined || storageKey === undefined) {
      setPreferenceState({ isReady: false, value: iv });
      return;
    }

    let cancelled = false;
    setPreferenceState({
      isReady: false,
      storageKey,
      value: iv,
    });

    const load = async () => {
      let nextValue = iv;
      try {
        const migrated = await migrateLegacyMultiUserPreference(key, storageKey, userId);
        if (migrated === undefined) {
          const stored = await getPreference<T>(storageKey);
          nextValue = stored !== undefined ? stored : iv;
        } else {
          nextValue = migrated as T;
        }
      } catch {
        nextValue = iv;
      }

      if (cancelled) {
        return;
      }

      // A user action or cross-tab update that completed during hydration wins.
      setPreferenceState(previousState =>
        previousState.storageKey === storageKey && !previousState.isReady
          ? { isReady: true, storageKey, value: nextValue }
          : previousState
      );
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [key, storageKey, userId]); // initialValue intentionally excluded — captured via ref

  useEffect(() => {
    if (!storageKey) return undefined;
    return subscribePreferenceChanges(({ key: changedKey, value }) => {
      const nextValue = value === undefined ? initialValueRef.current : (value as T);
      if (changedKey === storageKey && !isEqual(valueByUserRef.current, nextValue)) {
        valueByUserRef.current = nextValue;
        setPreferenceState({ isReady: true, storageKey, value: nextValue });
      }
    });
  }, [storageKey]);

  const setValue = useCallback(
    (value: T | ((previousValue: T) => T)) => {
      if (userId === undefined || storageKey === undefined) {
        return;
      }
      const nextValue = isFunction(value) ? value(valueByUserRef.current) : value;
      valueByUserRef.current = nextValue;
      setPreferenceState({ isReady: true, storageKey, value: nextValue });
      setPreference(storageKey, nextValue).catch(() => {
        // The preference storage layer owns fallback and diagnostic logging.
      });
    },
    [storageKey, userId]
  );

  return [valueByUser, setValue, isReady] as const;
};

export default useLocalStorageByUser;
